/** Admin settings page — retention, GDrive defaults, Gemini models (DD-13). */

import { useState, useEffect } from "react";
import {
  Box, Typography, TextField, Button, Paper, IconButton, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, CircularProgress, Alert,
  Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, List,
  ListItem, ListItemButton, ListItemIcon, ListItemText, Chip, MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import LabelIcon from "@mui/icons-material/Label";
import DescriptionIcon from "@mui/icons-material/Description";
import { DrivePicker, DrivePickerDocsView } from "@googleworkspace/drive-picker-react";
import { useAdminSettings, useUpdateSettings, useFetchGeminiModels } from "@/hooks/useDashboard";
import { getConfig } from "@/auth/config";
import { getUserManager } from "@/hooks/useAuth";

interface GeminiModel {
  value: string;
  label: string;
  detail: string;
}

interface DriveLabel {
  id: string;
  name: string;
  fields: { id: string; displayName: string; valueType: string; options?: { id: string; displayName: string }[] }[];
}

async function getAdminGDriveToken(): Promise<string> {
  const config = getConfig();
  const um = getUserManager();
  const user = await um?.getUser();
  const jwt = user?.id_token ?? "";
  const base = config?.b2bGatewayApiEndpoint;
  if (!base) {
    throw new Error(
      "Google Drive is not configured — b2bGatewayApiEndpoint is missing from config.json"
    );
  }
  const listResp = await fetch(`${base}/app-credentials?credentialTypeId=google_drive_oauth`, {
    headers: { Authorization: `Bearer ${jwt}` },
  });
  const listData = await listResp.json();
  const cred = listData.credentials?.[0];
  if (!cred) throw new Error("Connect Google Drive first");
  const valResp = await fetch(`${base}/app-credentials/${cred.appCredentialId}/value`, {
    headers: { Authorization: `Bearer ${jwt}`, "X-Credential-Access": "retrieve" },
  });
  const valData = await valResp.json();
  return valData.fields?.accessToken ?? "";
}

async function fetchDriveLabels(): Promise<DriveLabel[]> {
  const token = await getAdminGDriveToken();
  const res = await fetch(
    "https://drivelabels.googleapis.com/v2/labels?view=LABEL_VIEW_FULL&publishedOnly=true",
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) return [];
  const data = await res.json();
  return (data.labels ?? []).map((l: Record<string, unknown>) => ({
    id: (l.id as string) ?? "",
    name: ((l.properties as Record<string, unknown>)?.title as string) ?? (l.id as string),
    fields: ((l.fields as Record<string, unknown>[]) ?? []).map((f: Record<string, unknown>) => ({
      id: f.id,
      displayName: ((f.properties as Record<string, unknown>)?.displayName as string) ?? f.id,
      valueType: (f.valueType as string) ?? "TEXT",
      options: ((f.selectionOptions as Record<string, unknown>)?.choices as Record<string, unknown>[])?.map((c: Record<string, unknown>) => ({
        id: c.id,
        displayName: ((c.properties as Record<string, unknown>)?.displayName as string) ?? c.id,
      })) ?? [],
    })),
  }));
}

export default function Settings() {
  const { data, isLoading } = useAdminSettings();
  const updateSettings = useUpdateSettings();
  const fetchModels = useFetchGeminiModels();
  const [retention, setRetention] = useState(90);
  const [folderId, setFolderId] = useState("");
  const [folderName, setFolderName] = useState("");
  const [models, setModels] = useState<GeminiModel[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [pickerClientId, setPickerClientId] = useState("");
  const [pickerClientSecret, setPickerClientSecret] = useState("");
  const [pickerApiKey, setPickerApiKey] = useState("");
  const [defaultDocTemplateId, setDefaultDocTemplateId] = useState("");
  const [defaultDocTemplateName, setDefaultDocTemplateName] = useState("");
  const [mandatoryLabelId, setMandatoryLabelId] = useState("");
  const [mandatoryLabelName, setMandatoryLabelName] = useState("");
  const [defaultClassification, setDefaultClassification] = useState("");
  const [availableLabelIds, setAvailableLabelIds] = useState<string[]>([]);
  const [availableLabelNames, setAvailableLabelNames] = useState<Record<string, string>>({});
  const [newLabelId, setNewLabelId] = useState("");

  // Dynamic picker state
  const [templatePickerOpen, setTemplatePicker] = useState(false);
  const [folderPickerOpen, setFolderPickerOpen] = useState(false);
  const [pickerToken, setPickerToken] = useState<string | null>(null);
  const [labelDialogOpen, setLabelDialogOpen] = useState(false);
  const [driveLabels, setDriveLabels] = useState<DriveLabel[]>([]);
  const [labelsFetching, setLabelsFetching] = useState(false);
  const [labelDialogMode, setLabelDialogMode] = useState<"mandatory" | "available">("mandatory");
  const [classificationOptions, setClassificationOptions] = useState<{ id: string; displayName: string }[]>([]);

  useEffect(() => {
    if (data) {
      setRetention(data.retentionDays ?? 90);
      setFolderId(data.defaultGDriveFolderId ?? "");
      setFolderName(data.defaultGDriveFolderName ?? "");
      setModels(data.geminiModels ?? []);
      setPickerClientId(data.gdrivePickerConfig?.clientId ?? "");
      setPickerClientSecret(data.gdrivePickerConfig?.clientSecret ? "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" : "");
      setPickerApiKey(data.gdrivePickerConfig?.apiKey ? "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" : "");
      setDefaultDocTemplateId(data.defaultDocTemplateId ?? "");
      setDefaultDocTemplateName(data.defaultDocTemplateName ?? "");
      setMandatoryLabelId(data.mandatoryLabelId ?? "");
      setMandatoryLabelName(data.mandatoryLabelName ?? "");
      setDefaultClassification(data.defaultClassification ?? "");
      setAvailableLabelIds(data.availableLabelIds ?? []);
      setAvailableLabelNames(data.availableLabelNames ?? {});
    }
  }, [data]);

  const removeModel = (i: number) => setModels((m) => m.filter((_, idx) => idx !== i));
  const updateModel = (i: number, field: keyof GeminiModel, val: string) =>
    setModels((m) => m.map((model, idx) => (idx === i ? { ...model, [field]: val } : model)));

  const handleSave = () => {
    const pickerConfig: { clientId: string; clientSecret?: string; apiKey?: string } = { clientId: pickerClientId };
    const secretValue = pickerClientSecret === "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" ? undefined : pickerClientSecret;
    const apiKeyValue = pickerApiKey === "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022" ? undefined : pickerApiKey;
    if (secretValue) pickerConfig.clientSecret = secretValue;
    else if (data?.gdrivePickerConfig?.clientSecret) pickerConfig.clientSecret = data.gdrivePickerConfig.clientSecret;
    if (apiKeyValue) pickerConfig.apiKey = apiKeyValue;
    else if (data?.gdrivePickerConfig?.apiKey) pickerConfig.apiKey = data.gdrivePickerConfig.apiKey;

    updateSettings.mutate({
      retentionDays: retention,
      defaultGDriveFolderId: folderId,
      defaultGDriveFolderName: folderName || undefined,
      geminiModels: models,
      gdrivePickerConfig: pickerConfig.apiKey && pickerConfig.clientSecret ? pickerConfig as { clientId: string; clientSecret: string; apiKey: string } : undefined,
      defaultDocTemplateId: defaultDocTemplateId || undefined,
      defaultDocTemplateName: defaultDocTemplateName || undefined,
      mandatoryLabelId: mandatoryLabelId || undefined,
      mandatoryLabelName: mandatoryLabelName || undefined,
      defaultClassification: defaultClassification || undefined,
      availableLabelIds: availableLabelIds.length > 0 ? availableLabelIds : undefined,
      availableLabelNames: Object.keys(availableLabelNames).length > 0 ? availableLabelNames : undefined,
    });
  };

  // Browse Templates
  // Browse Labels
  const handleBrowseLabels = async (mode: "mandatory" | "available") => {
    setLabelDialogMode(mode);
    setLabelsFetching(true);
    try {
      const labels = await fetchDriveLabels();
      setDriveLabels(labels);
      setLabelDialogOpen(true);
    } catch { /* ignore */ }
    setLabelsFetching(false);
  };

  // Fetch classification options for the mandatory label
  const handleFetchClassificationOptions = async () => {
    if (!mandatoryLabelId) return;
    try {
      const token = await getAdminGDriveToken();
      const res = await fetch(
        `https://drivelabels.googleapis.com/v2/labels/${mandatoryLabelId}?view=LABEL_VIEW_FULL`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) return;
      const labelData = await res.json();
      // Find first selection field (classification)
      const selectionField = (labelData.fields ?? []).find((f: Record<string, unknown>) => f.selectionOptions);
      if (selectionField) {
        const choices = ((selectionField.selectionOptions as Record<string, unknown>)?.choices as Record<string, unknown>[]) ?? [];
        setClassificationOptions(choices.map((c: Record<string, unknown>) => ({
          id: ((c.properties as Record<string, unknown>)?.displayName as string) ?? (c.id as string),
          displayName: ((c.properties as Record<string, unknown>)?.displayName as string) ?? (c.id as string),
        })));
      }
    } catch { /* ignore */ }
  };

  const handleFetchModels = () => {
    fetchModels.refetch().then((result) => {
      if (result.data) {
        const existing = new Set(models.map((m) => m.value));
        setSelected(existing);
        setPickerOpen(true);
      }
    });
  };

  const toggleModel = (value: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(value)) next.delete(value); else next.add(value);
      return next;
    });
  };

  const handleAddSelected = () => {
    const available = fetchModels.data?.models ?? [];
    const existing = new Set(models.map((m) => m.value));
    const toAdd = available.filter((m) => selected.has(m.value) && !existing.has(m.value));
    setModels((prev) => [...prev, ...toAdd]);
    setModels((prev) => prev.filter((m) => selected.has(m.value)));
    setPickerOpen(false);
  };

  if (isLoading) return <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Settings</Typography>
      {updateSettings.isSuccess && <Alert severity="success" sx={{ mb: 2 }}>Settings saved</Alert>}
      {updateSettings.isError && <Alert severity="error" sx={{ mb: 2 }}>{updateSettings.error.message}</Alert>}

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Data Retention</Typography>
        <TextField label="Retention Days" type="number" value={retention} onChange={(e) => setRetention(Number(e.target.value))} size="small" sx={{ width: 200 }} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Google Drive Defaults</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            label="Default Folder"
            value={folderName}
            size="small"
            fullWidth
            placeholder="Select a folder using the picker"
            InputProps={{ readOnly: true }}
          />
          <Button size="small" onClick={async () => {
            if (!pickerClientId) {
              alert("Please configure the Google OAuth Client ID in the Google Picker Configuration section first.");
              return;
            }
            try {
              const token = await getAdminGDriveToken();
              setPickerToken(token);
              setFolderPickerOpen(true);
            } catch { /* user not connected */ }
          }} sx={{ whiteSpace: "nowrap" }}>
            Pick Folder
          </Button>
        </Box>
        {folderPickerOpen && pickerToken && (
          <DrivePicker
            oauth-token={pickerToken}
            app-id={pickerClientId?.split("-")[0] ?? ""}
            onPicked={(e: CustomEvent) => {
              const doc = e.detail?.docs?.[0];
              if (doc) { setFolderId(doc.id); setFolderName(doc.name); }
              setFolderPickerOpen(false);
            }}
            onCanceled={() => setFolderPickerOpen(false)}
          >
            <DrivePickerDocsView
              enable-drives="true"
              include-folders="true"
              select-folder-enabled="true"
              mime-types="application/vnd.google-apps.folder"
              mode="LIST"
            />
          </DrivePicker>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Google Picker Configuration</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Google OAuth Client ID and Picker API Key from the Google Cloud Console.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <TextField label="Google OAuth Client ID" value={pickerClientId} onChange={(e) => setPickerClientId(e.target.value)} size="small" fullWidth placeholder="123456789.apps.googleusercontent.com" />
          <TextField label="Google OAuth Client Secret" value={pickerClientSecret} onChange={(e) => setPickerClientSecret(e.target.value)} size="small" fullWidth placeholder="GOCSPX-..." onFocus={() => { if (pickerClientSecret === "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022") setPickerClientSecret(""); }} />
          <TextField label="Google Picker API Key" value={pickerApiKey} onChange={(e) => setPickerApiKey(e.target.value)} size="small" fullWidth placeholder="AIza..." onFocus={() => { if (pickerApiKey === "\u2022\u2022\u2022\u2022\u2022\u2022\u2022\u2022") setPickerApiKey(""); }} />
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Default Google Doc Template</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Template used when plugins create Google Docs. Users can override per-job.
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <TextField
            label="Template Name"
            value={defaultDocTemplateName}
            size="small"
            fullWidth
            placeholder={pickerClientId ? "Select a template using the picker" : "Configure Google OAuth Client ID first"}
            InputProps={{ readOnly: true }}
          />
          <Button startIcon={<DescriptionIcon />} size="small" onClick={async () => {
            if (!pickerClientId) {
              alert("Please configure the Google OAuth Client ID in the Google Picker Configuration section first.");
              return;
            }
            try {
              const token = await getAdminGDriveToken();
              setPickerToken(token);
              setTemplatePicker(true);
            } catch { /* user not connected */ }
          }} sx={{ whiteSpace: "nowrap" }}>
            Pick
          </Button>
        </Box>
        {templatePickerOpen && pickerToken && (
          <DrivePicker
            oauth-token={pickerToken}
            app-id={pickerClientId?.split("-")[0] ?? ""}
            onPicked={(e: CustomEvent) => {
              const doc = e.detail?.docs?.[0];
              if (doc) { setDefaultDocTemplateId(doc.id); setDefaultDocTemplateName(doc.name); }
              setTemplatePicker(false);
            }}
            onCanceled={() => setTemplatePicker(false)}
          >
            <DrivePickerDocsView
              view-id="DOCUMENTS"
              enable-drives="true"
              include-folders="true"
              mode="LIST"
            />
          </DrivePicker>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Mandatory Drive Label</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Applied automatically to every file created by plugins.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              label="Mandatory Label"
              value={mandatoryLabelName}
              size="small"
              fullWidth
              placeholder="Select a label using Browse"
              InputProps={{ readOnly: true }}
            />
            <Button startIcon={<LabelIcon />} size="small" onClick={() => handleBrowseLabels("mandatory")} disabled={labelsFetching} sx={{ whiteSpace: "nowrap" }}>
              {labelsFetching ? "Loading…" : "Browse"}
            </Button>
          </Box>
          <Box sx={{ display: "flex", gap: 1 }}>
            <TextField
              select
              label="Default Classification"
              value={defaultClassification}
              onChange={(e) => setDefaultClassification(e.target.value)}
              size="small"
              fullWidth
            >
              {classificationOptions.length > 0 ? (
                classificationOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.displayName}>{opt.displayName}</MenuItem>
                ))
              ) : defaultClassification ? (
                <MenuItem value={defaultClassification}>{defaultClassification}</MenuItem>
              ) : (
                <MenuItem disabled value="">No options loaded</MenuItem>
              )}
            </TextField>
            <Button size="small" onClick={handleFetchClassificationOptions} disabled={!mandatoryLabelId} sx={{ whiteSpace: "nowrap" }}>
              Fetch Options
            </Button>
          </Box>
        </Box>
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Available Drive Labels</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Labels that users can choose from when creating files.
        </Typography>
        <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
          <TextField
            label="Add Label ID"
            value={newLabelId}
            onChange={(e) => setNewLabelId(e.target.value)}
            size="small"
            fullWidth
            placeholder="labels/..."
          />
          <Button size="small" onClick={() => handleBrowseLabels("available")} disabled={labelsFetching} sx={{ whiteSpace: "nowrap" }}>
            Browse
          </Button>
          <Button
            size="small"
            onClick={() => {
              if (newLabelId.trim() && !availableLabelIds.includes(newLabelId.trim())) {
                setAvailableLabelIds([...availableLabelIds, newLabelId.trim()]);
                setNewLabelId("");
              }
            }}
            disabled={!newLabelId.trim()}
          >
            Add
          </Button>
        </Box>
        {availableLabelIds.length > 0 && (
          <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
            {availableLabelIds.map((id) => (
              <Chip key={id} label={availableLabelNames[id] || id} size="small" onDelete={() => { setAvailableLabelIds(availableLabelIds.filter((l) => l !== id)); const n = { ...availableLabelNames }; delete n[id]; setAvailableLabelNames(n); }} />
            ))}
          </Box>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
          <Typography variant="h6">Gemini Models</Typography>
          <Button startIcon={<CloudDownloadIcon />} size="small" onClick={handleFetchModels} disabled={fetchModels.isFetching}>
            {fetchModels.isFetching ? "Fetching…" : "Fetch from API"}
          </Button>
        </Box>
        {models.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 2 }}>No models configured. Click "Fetch from API" to get started.</Typography>
        )}
        {models.length > 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Value (API ID)</TableCell>
                  <TableCell>Label</TableCell>
                  <TableCell>Detail</TableCell>
                  <TableCell width={50} />
                </TableRow>
              </TableHead>
              <TableBody>
                {models.map((m, i) => (
                  <TableRow key={m.value}>
                    <TableCell><Typography variant="body2" sx={{ fontFamily: "monospace" }}>{m.value}</Typography></TableCell>
                    <TableCell><TextField value={m.label} onChange={(e) => updateModel(i, "label", e.target.value)} size="small" fullWidth /></TableCell>
                    <TableCell><TextField value={m.detail} onChange={(e) => updateModel(i, "detail", e.target.value)} size="small" fullWidth /></TableCell>
                    <TableCell><IconButton size="small" onClick={() => removeModel(i)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSave} disabled={updateSettings.isPending} size="large">
        Save Settings
      </Button>

      {/* Model Picker Dialog */}
      <Dialog open={pickerOpen} onClose={() => setPickerOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Select Gemini Models</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Select which models to make available to users.
          </Typography>
          <List dense sx={{ maxHeight: 400, overflow: "auto" }}>
            {(fetchModels.data?.models ?? []).map((m) => (
              <ListItem key={m.value} disablePadding>
                <ListItemButton onClick={() => toggleModel(m.value)} dense>
                  <ListItemIcon>
                    <Checkbox edge="start" checked={selected.has(m.value)} tabIndex={-1} disableRipple />
                  </ListItemIcon>
                  <ListItemText
                    primary={m.label}
                    secondary={`${m.value} — ${m.detail.slice(0, 80)}${m.detail.length > 80 ? "…" : ""}`}
                  />
                </ListItemButton>
              </ListItem>
            ))}
          </List>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPickerOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAddSelected}>Apply Selection ({selected.size})</Button>
        </DialogActions>
      </Dialog>

      {/* Label Browser Dialog */}
      <Dialog open={labelDialogOpen} onClose={() => setLabelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{labelDialogMode === "mandatory" ? "Select Mandatory Label" : "Add Available Label"}</DialogTitle>
        <DialogContent>
          {driveLabels.length === 0 ? (
            <Typography color="text.secondary" sx={{ py: 2 }}>No published Drive Labels found. Create labels at drive.google.com/labels.</Typography>
          ) : (
            <List dense sx={{ maxHeight: 400, overflow: "auto" }}>
              {driveLabels.map((l) => (
                <ListItem key={l.id} disablePadding>
                  <ListItemButton onClick={() => {
                    if (labelDialogMode === "mandatory") {
                      setMandatoryLabelId(l.id);
                      setMandatoryLabelName(l.name);
                      setClassificationOptions([]);
                      const selField = l.fields.find((f) => f.options && f.options.length > 0);
                      if (selField) {
                        setClassificationOptions(selField.options!.map((o) => ({ id: o.displayName, displayName: o.displayName })));
                      }
                    } else {
                      if (!availableLabelIds.includes(l.id)) {
                        setAvailableLabelIds([...availableLabelIds, l.id]);
                        setAvailableLabelNames({ ...availableLabelNames, [l.id]: l.name });
                      }
                    }
                    setLabelDialogOpen(false);
                  }}>
                    <ListItemIcon><LabelIcon /></ListItemIcon>
                    <ListItemText
                      primary={l.name}
                      secondary={`${l.id} — ${l.fields.length} field(s)`}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabelDialogOpen(false)}>Cancel</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
